import { format } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api'
import {
  IconCalendarDays,
  IconBuildings,
  IconReceiptBill,
  OverviewCard
} from '@sequencehq/core-components'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'

const humanReadableQuantity = (quantity: number = 0) => {
  if (quantity > 999) return '999+'
  return quantity
}

export const DraftBillingSchedulesCard = () => {
  const { data, isLoading } = useQuery(
    dashboard20240730Client.getBillingSchedules,
    {
      billingScheduleStatus: 'DRAFT',
      limit: 1
    }
  )

  return (
    <OverviewCard
      isLoading={isLoading}
      href="/billing-schedules?billingScheduleStatus=DRAFT"
      quantity={humanReadableQuantity(data?.pagination.totalResultSize)}
      title="Draft schedules"
      description={
        !isLoading && data?.pagination.totalResultSize === 0
          ? 'No draft schedules to review.'
          : 'Activate draft schedules to start billing cycles.'
      }
      icon={<IconCalendarDays />}
    />
  )
}

const useGetTotalInvoiceCount = () => {
  const invoicesQuery = useQuery(dashboardv20240509Client.getInvoices, {
    limit: 1,
    invoiceStatus: 'DRAFT'
  })

  return {
    isLoading: invoicesQuery.isPending,
    count: invoicesQuery.data?.pagination.totalResultSize
  }
}
export const DraftInvoicesCard = () => {
  const { isLoading, count: invoiceCount } = useGetTotalInvoiceCount()

  return (
    <OverviewCard
      isLoading={isLoading}
      href="/invoices?invoiceStatus=DRAFT"
      quantity={humanReadableQuantity(invoiceCount)}
      title="Draft invoices"
      description={
        !isLoading && invoiceCount === 0
          ? 'No draft invoices to review.'
          : 'Review and send invoices to customers.'
      }
      icon={<IconReceiptBill />}
    />
  )
}

export const OverdueInvoicesCard = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayFormatted = format(yesterday, 'yyyy-MM-dd')

  const { data, isLoading } = useQuery(dashboardv20240509Client.getInvoices, {
    invoiceStatus: 'SENT',
    invoicePaymentStatus: 'UNPAID',
    dueBefore: yesterdayFormatted,
    limit: 1
  })

  return (
    <OverviewCard
      isLoading={isLoading}
      href={`/invoices?invoiceStatus=SENT&invoicePaymentStatus=UNPAID&dueBefore=${yesterdayFormatted}`}
      variant="red"
      quantity={humanReadableQuantity(data?.pagination.totalResultSize)}
      title="Overdue invoices"
      description={
        !isLoading && data?.pagination.totalResultSize === 0
          ? 'No overdue invoices.'
          : 'Send reminders for overdue invoices.'
      }
      icon={<IconReceiptBill />}
    />
  )
}

export const UnbilledCustomersCard = () => {
  const { data, isLoading } = useQuery(dashboard20240730Client.getCustomers, {
    billingScheduleStatus: 'DRAFT',
    limit: 1
  })

  return (
    <OverviewCard
      isLoading={isLoading}
      href="/customers"
      quantity={data?.pagination.totalResultSize ?? 0}
      title="Total customers"
      description={
        !isLoading && data?.pagination.totalResultSize === 0
          ? 'Start creating customers.'
          : 'Create new schedules for unbilled customers.'
      }
      icon={<IconBuildings />}
    />
  )
}
